<script setup lang="ts">
import { useDivisible } from '@precon-tools-suite-by-shared/vue-divisibles';
import { computed, useSlots } from 'vue';
import Skeleton from 'primevue/skeleton';
import Button from 'primevue/button';

const props = defineProps<{
    title?: string;
    titleLoading?: boolean;
    noTitle?: boolean;

    subtitle?: string;
    subtitleLoading?: boolean;
    noSubtitle?: boolean;

    description?: string;
    descriptionLoading?: boolean;
    noDescription?: boolean;

    headerLoading?: boolean;
    noHeader?: boolean;

    toolsLoading?: boolean;
    noTools?: boolean;

    footerLoading?: boolean;
    noFooter?: boolean;

    actionsLoading?: boolean;
    noActions?: boolean;

    footer?: string;
}>();

const { give } = useDivisible();
const slots = useSlots();

// const givePart = () => {

// }

const { observed: titleObserved } = give.slot('title', {
    fallback: () => props.title,
    loading: () => props.titleLoading,
    show: () => !props.noHeader && !props.noTitle,
});
const { observed: subtitleObserved } = give.slot('subtitle', {
    fallback: () => props.subtitle,
    loading: () => props.subtitleLoading,
    show: () => !props.noHeader && !props.noSubtitle,
});
const { observed: headerObserved } = give.slot('header', {
    loading: () => props.headerLoading,
    show: () => !props.noHeader,
});
const { observed: toolsObserved } = give.slot('tools', {
    loading: () => props.toolsLoading,
    show: () => !props.noTools,
});
const { observed: descriptionObserved } = give.slot('description', {
    fallback: () => props.description,
    loading: () => props.descriptionLoading,
    show: () => !props.noDescription,
});

const { observed: footerObserved } = give.slot('footer', {
    fallback: () => props.footer,
    loading: () => props.footerLoading,
    show: () => !props.noFooter,
});

const { observed: actionsObserved } = give.slot('actions', {
    loading: () => props.actionsLoading,
    show: () => !props.noActions,
});

const showTitle = computed<boolean>(() => {
    if (props.noTitle) return false;
    if (titleObserved.value) return false;
    return !!(slots.title || props.title || props.titleLoading);
});
const showSubTitle = computed<boolean>(() => {
    if (props.noSubtitle) return false;
    if (subtitleObserved.value) return false;
    return !!(slots.subtitle || props.subtitle || props.subtitleLoading);
});

const showTools = computed(() => {
    if (props.noTools) return false;
    if (toolsObserved.value) return false;
    return slots.tools || props.toolsLoading;
});

const showHeader = computed(() => {
    if (props.noHeader) return false;
    return (
        (!headerObserved.value && (slots.header || props.headerLoading)) ||
        showTitle.value ||
        showSubTitle.value
    );
});

const showDescription = computed(() => {
    if (descriptionObserved.value) return false;
    return !!(
        slots.description ||
        props.description ||
        props.descriptionLoading
    );
});

const showFooter = computed(() => {
    if (footerObserved.value) return false;
    return !!(slots.footer || props.footer || props.footerLoading);
});

const showActions = computed(() => {
    if (actionsObserved.value) return false;
    return !!slots.actions || props.actionsLoading;
});
</script>

<template>
    <section>
        <div v-if="showHeader" class="dynamic-section-header flex">
            <div class="flex-grow-1">
                <slot name="header">
                    <h5 v-if="showTitle" class="dynamic-section-title mb-0">
                        <template v-if="titleLoading">
                            <Skeleton style="height: 2rem; width: 12rem" />
                        </template>
                        <template v-else>
                            <slot name="title">
                                {{ title }}
                            </slot>
                        </template>
                    </h5>
                    <div
                        v-if="showSubTitle"
                        class="dynamic-section-subtitle"
                        :class="{ loading: subtitleLoading }"
                    >
                        <template v-if="subtitleLoading">
                            <Skeleton style="height: 1rem; width: 16rem" />
                        </template>
                        <template v-else>
                            <slot name="subtitle">
                                {{ subtitle }}
                            </slot>
                        </template>
                    </div>
                </slot>
            </div>
            <template v-if="showTools">
                <template v-if="toolsLoading">
                    <Button
                        class="p-button-rounded p-button-text p-button-secondary"
                        icon="pi pi-spinner pi-spin"
                        disabled
                    />
                </template>
                <template v-else>
                    <slot name="tools" />
                </template>
            </template>
        </div>
        <hr v-if="showHeader" />

        <template v-if="!showHeader && showTools">
            <div class="flex">
                <div class="flex-grow-1">
                    <p
                        v-if="showDescription"
                        class="flex-grow-1 dynamic-section-description"
                    >
                        <slot name="description">
                            {{ description }}
                        </slot>
                    </p>
                </div>
                <template v-if="toolsLoading">
                    <Button
                        class="p-button-rounded p-button-text p-button-secondary"
                        icon="pi pi-spinner pi-spin"
                        disabled
                    />
                </template>
                <template v-else>
                    <slot name="tools" />
                </template>
            </div>
        </template>
        <template v-else>
            <p v-if="showDescription" class="dynamic-section-description">
                <template v-if="descriptionLoading">
                    <Skeleton style="width: 100%; display: inline-block" />
                    <Skeleton style="width: 40%; display: inline-block" />
                </template>
                <template v-else>
                    <slot name="description">
                        {{ description }}
                    </slot>
                </template>
            </p>
        </template>

        <slot />

        <template v-if="showActions || showFooter">
            <hr />
            <div class="dynamic-section-footer flex gap-2">
                <div class="flex-grow-1 text-sm">
                    <template v-if="footerLoading">
                        <Skeleton
                            style="
                                width: 7rem;
                                max-width: 50%;
                                margin-bottom: 0.25rem;
                            "
                        />
                        <Skeleton style="width: 14rem; max-width: 80%" />
                    </template>
                    <template v-else>
                        <slot name="footer">
                            {{ footer }}
                        </slot>
                    </template>
                </div>
                <template v-if="actionsLoading">
                    <Skeleton style="height: 2.2rem; width: 8rem" />
                    <Skeleton style="height: 2.2rem; width: 6rem" />
                </template>
                <template v-else>
                    <slot name="actions" />
                </template>
            </div>
        </template>
    </section>
</template>

<style scoped lang="scss">
.dynamic-section-subtitle {
    color: var(--gray-400);
    font-size: 14px;
    font-weight: 600;

    &.loading {
        margin-top: 0.25rem;
    }
}
</style>
